import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, Loader2, Copy, Check, LogIn, ArrowLeft } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SharedChatData {
  session_id: number;
  title: string | null;
  messages: ChatMessage[];
  survey_id: number;
  created_at: string;
  can_fork: boolean;
  view_count: number;
  is_owner: boolean;
}

// Markdown components
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>
  ),
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-[#07E5D1] hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

const SharedSurveyChatView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const chatScrollRef = useRef<HTMLDivElement>(null);
  const hasAutoForked = useRef(false);

  const [sharedChat, setSharedChat] = useState<SharedChatData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isForking, setIsForking] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchSharedChat = async () => {
      if (!shareToken) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        const fetchOptions: RequestInit = isAuthenticated ? { credentials: 'include' } : {};

        const res = await fetch(`${API_URL}/survey-chat/shared/${shareToken}`, fetchOptions);
        if (!res.ok) {
          if (res.status === 404) {
            setError('This shared chat was not found or has expired');
          } else {
            setError('Failed to load shared chat');
          }
          setIsLoading(false);
          return;
        }

        const data = await res.json();
        setSharedChat(data);
      } catch (err) {
        console.error('Error fetching shared chat:', err);
        setError('Failed to load shared chat');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedChat();
  }, [shareToken, isAuthenticated]);

  // Auto-fork if returning from login with fork=true param
  useEffect(() => {
    const shouldAutoFork = searchParams.get('fork') === 'true';

    if (
      shouldAutoFork &&
      isAuthenticated &&
      sharedChat &&
      !sharedChat.is_owner &&
      sharedChat.can_fork &&
      !hasAutoForked.current &&
      !isForking
    ) {
      hasAutoForked.current = true;
      handleFork();
    }
  }, [searchParams, isAuthenticated, sharedChat, isForking]);

  const handleFork = async () => {
    if (!shareToken || !sharedChat) return;

    setIsForking(true);
    try {
      const res = await fetch(`${API_URL}/survey-chat/shared/${shareToken}/fork`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Failed to fork chat');
      }

      const data = await res.json();
      navigate(`/surveys/${sharedChat.survey_id}/chat/${data.new_session_id}`);
    } catch (err) {
      console.error('Error forking chat:', err);
      setIsForking(false);
    }
  };

  const handleContinueClick = () => {
    if (!isAuthenticated) {
      const returnUrl = encodeURIComponent(`${window.location.pathname}?fork=true`);
      navigate(`/auth?redirect=${returnUrl}`);
      return;
    }
    handleFork();
  };

  const handleCopyMessage = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
          <p className="text-sm text-[#6B7280]">Loading shared chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-[#1C1C1E] mb-2">Chat Not Found</h1>
          <p className="text-sm text-[#6B7280] mb-6">{error}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#07E5D1] text-white rounded-lg hover:bg-[#06D4C2] transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Home
          </a>
        </div>
      </div>
    );
  }

  if (!sharedChat) return null;

  return (
    <div className="min-h-screen bg-[#FEFEFE] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-[#F1F5F9] px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
              <ClipboardList className="w-4.5 h-4.5" />
            </div>
            <div>
              <h1 className="font-semibold text-[#1C1C1E] text-sm">
                {sharedChat.title || 'Shared Survey Chat'}
              </h1>
              <p className="text-xs text-[#9CA3AF]">
                {sharedChat.messages.length} messages · {sharedChat.view_count} views
              </p>
            </div>
          </div>

          <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="h-12" />
          </a>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto" ref={chatScrollRef}>
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="space-y-5">
            {sharedChat.messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`rounded-2xl px-5 py-4 ${
                    msg.role === 'user'
                      ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                      : 'max-w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div className="max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.content}
                      </ReactMarkdown>
                      <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                        <button
                          onClick={() => handleCopyMessage(msg.content, idx)}
                          className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                            copiedMessageIndex === idx
                              ? 'text-[#10B981] bg-[#ECFDF5]'
                              : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                          }`}
                        >
                          {copiedMessageIndex === idx ? (
                            <>
                              <Check className="w-3.5 h-3.5" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer CTA */}
      <footer className="bg-white border-t border-[#F1F5F9] px-6 py-4">
        <div className="max-w-4xl mx-auto">
          {sharedChat.is_owner ? (
            <button
              onClick={() =>
                navigate(`/surveys/${sharedChat.survey_id}/chat/${sharedChat.session_id}`)
              }
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#07E5D1] text-white rounded-xl hover:bg-[#06D4C2] transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to My Chat
            </button>
          ) : sharedChat.can_fork ? (
            <button
              onClick={handleContinueClick}
              disabled={isForking}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#07E5D1] text-white rounded-xl hover:bg-[#06D4C2] transition-colors font-medium disabled:opacity-50"
            >
              {isForking ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating your copy...
                </>
              ) : isAuthenticated ? (
                <>
                  <ClipboardList className="w-4 h-4" />
                  Continue This Chat
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign in to Continue Chat
                </>
              )}
            </button>
          ) : null}
        </div>
      </footer>
    </div>
  );
};

export default SharedSurveyChatView;
